import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';

const DEFAULT_TEMPLATES = [
  {
    templateKey: 'lesson_follow_up',
    name: 'Lesson Follow-Up',
    body: 'Hi {{clientName}}, great work in your recent session. {{progressSummary}} A strong next step would be {{suggestedNextService}}.',
    category: 'follow_up',
  },
  {
    templateKey: 'rebook_reminder',
    name: 'Rebook Reminder',
    body: 'Hi {{clientName}}, I wanted to check in after {{lastService}} on {{lastLessonDate}}. {{nextFocus}} If you are ready, {{suggestedNextService}} would be a great next booking.',
    category: 'rebook',
  },
  {
    templateKey: 'package_offer',
    name: 'Package Offer',
    body: 'Hi {{clientName}}, based on your recent progress, {{packageName}} could be a strong fit. {{progressSummary}}',
    category: 'package',
  },
  {
    templateKey: 'membership_renewal',
    name: 'Membership Renewal',
    body: 'Hi {{clientName}}, your current membership is approaching renewal. {{suggestedNextService}} is available whenever you are ready to keep momentum going.',
    category: 'renewal',
  },
  {
    templateKey: 'inactive_client_checkin',
    name: 'Inactive Client Check-In',
    body: 'Hi {{clientName}}, I wanted to check in since it has been a little while. {{progressSummary}} Let me know if {{suggestedNextService}} would be helpful.',
    category: 'checkin',
  },
  {
    templateKey: 'post_video_feedback_followup',
    name: 'Post-Feedback Follow-Up',
    body: 'Hi {{clientName}}, after reviewing your recent work, {{nextFocus}}. {{progressSummary}} If you want, we can book {{suggestedNextService}} next.',
    category: 'feedback',
  },
];

function normalizeString(value = '') {
  return String(value || '').trim();
}

export function normalizeRetentionMessageTemplate(record = {}, fallbackId = '') {
  const id = record.id || record.templateId || fallbackId || '';
  return {
    id,
    templateId: id,
    templateKey: normalizeString(record.templateKey || ''),
    organizationId: record.organizationId || '',
    name: normalizeString(record.name || record.templateKey || 'Template'),
    body: normalizeString(record.body || ''),
    category: normalizeString(record.category || 'custom'),
    actionType: normalizeString(record.actionType || record.category || ''),
    active: record.active !== false,
    isBuiltIn: record.isBuiltIn === true,
    isDefault: record.isDefault === true,
    templateSource: normalizeString(record.templateSource || (record.isBuiltIn ? 'built_in' : 'organization')),
    createdByUserId: record.createdByUserId || '',
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export async function getOrganizationTemplates(db, organizationId = '', { includeInactive = false } = {}) {
  if (!organizationId) return [];
  const snapshot = await getDocs(
    query(
      collection(db, PLATFORM_COLLECTIONS.RETENTION_MESSAGE_TEMPLATES),
      where('organizationId', '==', organizationId)
    )
  );

  return (snapshot.docs || [])
    .map((docSnap) => normalizeRetentionMessageTemplate({ id: docSnap.id, ...docSnap.data() }))
    .filter((template) => includeInactive || template.active);
}

export async function getRetentionMessageTemplates(db, { organizationId = '', includeInactive = false } = {}) {
  const organizationTemplates = await getOrganizationTemplates(db, organizationId, { includeInactive });
  const seenKeys = new Set();
  const mergedTemplates = [...organizationTemplates, ...DEFAULT_TEMPLATES]
    .map((template, index) => normalizeRetentionMessageTemplate(template, template.id || `default-${index}`))
    .filter((template) => {
      const key = template.templateKey || template.name;
      if (!key || seenKeys.has(key)) return false;
      seenKeys.add(key);
      return includeInactive || template.active;
    });

  return mergedTemplates;
}

export function buildMessageFromTemplate(template = {}, variables = {}) {
  const templateBody = normalizeString(template.body || '');
  const replacements = {
    clientName: normalizeString(variables.clientName || 'there'),
    instructorName: normalizeString(variables.instructorName || 'your coach'),
    lastService: normalizeString(variables.lastService || 'your recent session'),
    lastLessonDate: normalizeString(variables.lastLessonDate || 'recently'),
    suggestedNextService: normalizeString(variables.suggestedNextService || 'the next session'),
    packageName: normalizeString(variables.packageName || 'a coaching package'),
    progressSummary: normalizeString(variables.progressSummary || ''),
    nextFocus: normalizeString(variables.nextFocus || 'the next coaching focus looks clear'),
  };

  return templateBody.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => replacements[key] || '');
}

export async function saveCustomTemplate(
  db,
  {
    organizationId = '',
    templateData = {},
    createdByUserId = '',
  } = {}
) {
  if (!organizationId || !createdByUserId) {
    throw new Error('organizationId and createdByUserId are required.');
  }

  const normalized = normalizeRetentionMessageTemplate({
    organizationId,
    ...templateData,
    createdByUserId,
    active: templateData.active !== false,
    isBuiltIn: false,
    templateSource: 'organization',
  });

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.RETENTION_MESSAGE_TEMPLATES), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return normalizeRetentionMessageTemplate(normalized, docRef.id);
}

export async function updateRetentionTemplate(
  db,
  {
    templateId = '',
    updates = {},
  } = {}
) {
  if (!templateId) {
    throw new Error('templateId is required.');
  }

  const normalized = normalizeRetentionMessageTemplate({
    ...updates,
  }, templateId);

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.RETENTION_MESSAGE_TEMPLATES, templateId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  });

  return normalized;
}

export async function archiveRetentionTemplate(db, { templateId = '' } = {}) {
  return updateRetentionTemplate(db, {
    templateId,
    updates: {
      active: false,
      isDefault: false,
    },
  });
}

export async function setDefaultRetentionTemplate(
  db,
  {
    organizationId = '',
    templateId = '',
  } = {}
) {
  if (!organizationId || !templateId) {
    throw new Error('organizationId and templateId are required.');
  }

  const organizationTemplates = await getOrganizationTemplates(db, organizationId, { includeInactive: true });
  const targetTemplate = organizationTemplates.find((template) => template.templateId === templateId);
  if (!targetTemplate) {
    throw new Error('Only organization templates can be set as the default template.');
  }

  const actionType = targetTemplate.actionType || targetTemplate.category || '';
  await Promise.all(
    organizationTemplates
      .filter((template) => (template.actionType || template.category || '') === actionType)
      .map((template) => updateDoc(doc(db, PLATFORM_COLLECTIONS.RETENTION_MESSAGE_TEMPLATES, template.templateId), {
        isDefault: template.templateId === templateId,
        updatedAt: serverTimestamp(),
      }))
  );

  return {
    ...targetTemplate,
    isDefault: true,
  };
}

export function getDefaultRetentionTemplate(
  templates = [],
  {
    templateKey = '',
    actionType = '',
  } = {}
) {
  if (templateKey) {
    const explicit = (templates || []).find((template) => template.templateKey === templateKey && template.active !== false);
    if (explicit) return explicit;
  }

  const defaultTemplate = (templates || []).find((template) =>
    template.active !== false &&
    template.isDefault === true &&
    (!actionType || (template.actionType || template.category) === actionType)
  );
  if (defaultTemplate) return defaultTemplate;

  return (templates || []).find((template) =>
    template.active !== false &&
    (!actionType || (template.actionType || template.category) === actionType)
  ) || null;
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimestampValue(value) {
  return toDateValue(value)?.getTime?.() || 0;
}

export function getTemplateUsageSummary(
  actions = [],
  templates = [],
  {
    bookings = [],
    memberships = [],
  } = {}
) {
  const templateMap = new Map(
    (templates || []).map((template) => [template.templateKey, normalizeRetentionMessageTemplate(template, template.templateId || template.id || '')])
  );

  const usageMap = new Map();
  (actions || []).forEach((action) => {
    const templateKey = normalizeString(action.templateKey || '');
    if (!templateKey) return;
    const current = usageMap.get(templateKey) || {
      templateKey,
      templateName: templateMap.get(templateKey)?.name || templateKey,
      usageCount: 0,
      followUpSentCount: 0,
      packageOfferCount: 0,
      rebookConversionCount: 0,
      packageConversionCount: 0,
      lastUsedAt: null,
    };
    current.usageCount += 1;
    if (action.actionType === 'followup_sent') current.followUpSentCount += 1;
    if (action.actionType === 'package_offered') current.packageOfferCount += 1;
    if (
      action.actionType === 'followup_sent' &&
      (bookings || []).some((booking) =>
        booking.requesterUserId === action.clientUserId &&
        toTimestampValue(booking.createdAt || booking.startAt) >= toTimestampValue(action.createdAt)
      )
    ) {
      current.rebookConversionCount += 1;
    }
    if (
      action.actionType === 'package_offered' &&
      (memberships || []).some((membership) =>
        membership.userId === action.clientUserId &&
        toTimestampValue(membership.createdAt || membership.startAt) >= toTimestampValue(action.createdAt)
      )
    ) {
      current.packageConversionCount += 1;
    }
    current.lastUsedAt = action.createdAt || current.lastUsedAt;
    usageMap.set(templateKey, current);
  });

  return Array.from(usageMap.values()).sort((left, right) => right.usageCount - left.usageCount);
}

export const RETENTION_TEMPLATE_VARIABLES = [
  'clientName',
  'instructorName',
  'lastService',
  'lastLessonDate',
  'suggestedNextService',
  'packageName',
  'progressSummary',
  'nextFocus',
];
